import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js'
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js'
import { Lensflare, LensflareElement } from 'three/addons/objects/Lensflare.js'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'

const log = (...args) => console.log('[CosmicStage:scene]', ...args)
const warn = (...args) => console.warn('[CosmicStage:scene]', ...args)
const err = (...args) => console.error('[CosmicStage:scene]', ...args)

log('scene.js loaded')

const ASSET_BASE = 'https://storage.googleapis.com/thecosmicstage-assets/3D_files/planets'

// ═══════════════════════════════════════════════════════════════
// GLSL SHADERS
// ═══════════════════════════════════════════════════════════════

const SIMPLEX_3D_GLSL = `
vec3 mod289(vec3 x){return x-floor(x*(1.0/289.0))*289.0;}
vec4 mod289(vec4 x){return x-floor(x*(1.0/289.0))*289.0;}
vec4 permute(vec4 x){return mod289((x*34.0+1.0)*x);}
vec4 taylorInvSqrt(vec4 r){return 1.79284291400159-0.85373472095314*r;}
float snoise(vec3 v){
  const vec2 C=vec2(1.0/6.0,1.0/3.0);
  const vec4 D=vec4(0.0,0.5,1.0,2.0);
  vec3 i=floor(v+dot(v,C.yyy));
  vec3 x0=v-i+dot(i,C.xxx);
  vec3 g=step(x0.yzx,x0.xyz);
  vec3 l=1.0-g;
  vec3 i1=min(g.xyz,l.zxy);
  vec3 i2=max(g.xyz,l.zxy);
  vec3 x1=x0-i1+C.xxx;
  vec3 x2=x0-i2+C.yyy;
  vec3 x3=x0-D.yyy;
  i=mod289(i);
  vec4 p=permute(permute(permute(
    i.z+vec4(0.0,i1.z,i2.z,1.0))
    +i.y+vec4(0.0,i1.y,i2.y,1.0))
    +i.x+vec4(0.0,i1.x,i2.x,1.0));
  float n_=0.142857142857;
  vec3 ns=n_*D.wyz-D.xzx;
  vec4 j=p-49.0*floor(p*ns.z*ns.z);
  vec4 x_=floor(j*ns.z);
  vec4 y_=floor(j-7.0*x_);
  vec4 x=x_*ns.x+ns.yyyy;
  vec4 y=y_*ns.x+ns.yyyy;
  vec4 h=1.0-abs(x)-abs(y);
  vec4 b0=vec4(x.xy,y.xy);
  vec4 b1=vec4(x.zw,y.zw);
  vec4 s0=floor(b0)*2.0+1.0;
  vec4 s1=floor(b1)*2.0+1.0;
  vec4 sh=-step(h,vec4(0.0));
  vec4 a0=b0.xzyw+s0.xzyw*sh.xxyy;
  vec4 a1=b1.xzyw+s1.xzyw*sh.zzww;
  vec3 p0=vec3(a0.xy,h.x);
  vec3 p1=vec3(a0.zw,h.y);
  vec3 p2=vec3(a1.xy,h.z);
  vec3 p3=vec3(a1.zw,h.w);
  vec4 norm=taylorInvSqrt(vec4(dot(p0,p0),dot(p1,p1),dot(p2,p2),dot(p3,p3)));
  p0*=norm.x;p1*=norm.y;p2*=norm.z;p3*=norm.w;
  vec4 m=max(0.6-vec4(dot(x0,x0),dot(x1,x1),dot(x2,x2),dot(x3,x3)),0.0);
  m=m*m;
  return 42.0*dot(m*m,vec4(dot(p0,x0),dot(p1,x1),dot(p2,x2),dot(p3,x3)));
}
`

const sunVertexShader = `
varying vec3 vPosition;
void main(){
  vPosition=position;
  gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);
}
`

const sunFragmentShader = `
uniform float uTime;
varying vec3 vPosition;
${SIMPLEX_3D_GLSL}
float fbm(vec3 p){
  float v=0.0;float a=0.5;
  for(int i=0;i<6;i++){v+=a*snoise(p);p*=2.0;a*=0.5;}
  return v;
}
void main(){
  vec3 pos=normalize(vPosition)*2.5;
  float n1=fbm(pos+uTime*0.035);
  float n2=fbm(pos*1.4-uTime*0.05+10.0);
  float n3=fbm(pos*0.6+uTime*0.02+20.0);
  float n=n1*0.5+n2*0.3+n3*0.2;
  n=n*0.5+0.5;
  vec3 darkSpot=vec3(0.7,0.12,0.0);
  vec3 surface=vec3(1.0,0.42,0.04);
  vec3 bright=vec3(1.0,0.82,0.28);
  vec3 hotSpot=vec3(1.0,0.95,0.75);
  vec3 color;
  if(n<0.3) color=mix(darkSpot,surface,n/0.3);
  else if(n<0.6) color=mix(surface,bright,(n-0.3)/0.3);
  else color=mix(bright,hotSpot,(n-0.6)/0.4);
  color*=2.2;
  gl_FragColor=vec4(color,1.0);
}
`

// ═══════════════════════════════════════════════════════════════
// BLACK HOLE SHADERS (ported from standalone Black Hole project)
// ═══════════════════════════════════════════════════════════════

const BH_DISC_VERT = `
uniform mat4 projectionMatrix;
uniform mat4 modelViewMatrix;
in vec3 position;
in vec2 uv;
out vec2 vUv;
void main() {
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  vUv = uv;
}`

const BH_DISC_FRAG = `
precision highp float;
precision highp int;
uniform float uTime;
uniform sampler2D uNoiseTexture;
uniform vec3 uInnerColor;
uniform vec3 uOuterColor;
uniform float uInnerEdge;
in vec2 vUv;
layout(location = 0) out vec4 pc_FragColor;

float inverseLerp(float v, float minValue, float maxValue) {
  return (v - minValue) / (maxValue - minValue);
}
vec3 blendAdd(vec3 base, vec3 blend) { return min(base + blend, vec3(1.0)); }

void main() {
  vec4 color = vec4(0.0);
  color.a = 1.0;
  float iterations = 3.0;

  for(float i = 0.0; i < iterations; i++) {
    float progress = i / (iterations - 1.0);
    float intensity = 1.0 - ((vUv.y - progress) * iterations) * 0.5;
    intensity = smoothstep(0.0, 1.0, intensity);

    vec2 uv = vUv;
    uv.y *= 2.0;
    float warpNoise = texture(uNoiseTexture, vec2(uv.x + uTime * 0.05, uv.y)).r;
    uv.y += (warpNoise - 0.5) * 0.3;
    uv.x += uTime / ((i * 20.0) + 3.0);

    vec3 ringColor = mix(uInnerColor, uOuterColor, progress);
    float noiseIntensity = texture(uNoiseTexture, uv).r;
    ringColor = mix(vec3(0.0), ringColor.rgb, noiseIntensity * intensity);
    color.rgb = blendAdd(color.rgb, ringColor);
  }

  float edgesAttenuation = min(inverseLerp(vUv.y, 0.0, 0.02), inverseLerp(vUv.y, 1.0, 0.5));
  edgesAttenuation *= smoothstep(uInnerEdge - 0.02, uInnerEdge + 0.05, vUv.y);
  color.rgb = mix(vec3(0.0), color.rgb, edgesAttenuation);
  pc_FragColor = color;
}`

const BH_PARTICLE_VERT = `
#define PI 3.1415926538
uniform mat4 projectionMatrix;
uniform mat4 modelViewMatrix;
uniform float uTime;
uniform vec3 uInnerColor;
uniform vec3 uOuterColor;
uniform float uViewHeight;
uniform float uSize;
uniform float uNumSwarms;
uniform float uDiscWidth;

in float aDistance;
in float aSize;
in float aRandom;
out vec3 vColor;

void main() {
  float concentration = 0.05;
  float outerProgress = smoothstep(0.0, 1.0, aDistance);
  outerProgress = mix(concentration, outerProgress, pow(aRandom, 1.7));
  float radius = 1.0 + outerProgress * uDiscWidth;

  float speed = pow(1.0 - outerProgress, 3.0) * 1.5;
  float angle = outerProgress - uTime * speed;
  float n = max(uNumSwarms, 1.0);
  float swarmId = min(floor(aRandom * n), n - 1.0);
  angle += swarmId * (2.0 * PI / n);

  float yOffset = (aRandom - 0.5) * 0.8 * (1.0 - outerProgress);

  vec3 newPosition = vec3(
    sin(angle) * radius,
    yOffset,
    cos(angle) * radius
  );
  vec4 modelViewPosition = modelViewMatrix * vec4(newPosition, 1.0);
  gl_Position = projectionMatrix * modelViewPosition;

  float sizeFalloff = smoothstep(0.6, 0.3, outerProgress);
  gl_PointSize = aSize * uSize * uViewHeight * sizeFalloff;
  gl_PointSize *= (1.0 / -modelViewPosition.z);
  if(outerProgress > 0.65) gl_PointSize = 0.0;

  float t = pow(outerProgress, 0.5);
  vColor = mix(uInnerColor, uOuterColor, t);
}`

const BH_PARTICLE_FRAG = `
precision highp float;
precision highp int;
layout(location = 0) out vec4 pc_FragColor;
in vec3 vColor;
void main() {
  float distanceToCenter = length(gl_PointCoord - vec2(0.5));
  float alpha = exp(-distanceToCenter * distanceToCenter * 12.0) * 0.25;
  if(alpha < 0.01) discard;
  pc_FragColor = vec4(vColor, alpha);
}`

// ═══════════════════════════════════════════════════════════════
// 3D BODY DEFINITIONS
// ═══════════════════════════════════════════════════════════════

export const DEFAULT_TARGET = { x: -2, y: 0.2, z: 0 }

export function isMobile() {
  return window.innerWidth <= 932 && 'ontouchstart' in window
}

const MOBILE_CAM_PULLBACK = 1.4

// Synced with Solar System BODIES (materials, lighting, camera)
export const BODY_3D = {
  sun: {
    modelName: 'the_star_sun', radius: 5, type: 'star',
    cameraDist: 19.1, cameraAngle: 0.47, tilt: 0.098,
    rotationSpeed: 0.0003,
    lightIntensity: 1.55, ambientIntensity: 0.35,
    bloomStrength: 0.55, bloomThreshold: 0.62,
    metalness: 0, roughness: 0.48,
    cameraPos: { x: 2.509, y: 0.014, z: -2.059 },
    cameraTarget: { x: 1.15, y: -0.751, z: -3.097 },
  },
  mercury: {
    modelName: 'mercury', radius: 1.3, type: 'planet',
    cameraDist: 4.8, cameraAngle: 0.5, tilt: 0.03,
    rotationSpeed: 0.0015,
    lightIntensity: 5, ambientIntensity: 1,
    bloomStrength: 0.55, bloomThreshold: 0.62,
    metalness: 0, roughness: 1,
    cameraPos: { x: 1.714, y: -0.061, z: -1.745 },
  },
  venus: {
    modelName: 'venus', radius: 1.8, type: 'planet',
    cameraDist: 6.5, cameraAngle: 0.45, tilt: 0.728,
    rotationSpeed: -0.0001,
    lightIntensity: 4.15, ambientIntensity: 0,
    bloomStrength: 0.55, bloomThreshold: 0.62,
    cameraPos: { x: 1.714, y: -0.061, z: -1.745 },
  },
  earth: {
    modelName: 'earth', radius: 2.0, type: 'planet',
    cameraDist: 9.9, cameraAngle: 0.35, tilt: -0.072,
    rotationSpeed: 0.0003,
    lightIntensity: 2.9, ambientIntensity: 0.35,
    bloomStrength: 0.55, bloomThreshold: 0.62,
    metalness: 1, roughness: 1,
    cameraPos: { x: 2.419, y: 0.206, z: -1.598 },
    cameraTarget: { x: -2.115, y: -0.055, z: -1.024 },
  },
  mars: {
    modelName: 'mars', radius: 1.5, type: 'planet',
    cameraDist: 5.5, cameraAngle: 0.5, tilt: 0.44,
    rotationSpeed: 0.002,
    lightIntensity: 2.5, ambientIntensity: 0.6,
    bloomStrength: 0.55, bloomThreshold: 0.62,
    cameraPos: { x: 1.714, y: -0.061, z: -1.745 },
  },
  jupiter: {
    modelName: 'jupiter', radius: 4.0, type: 'planet',
    cameraDist: 12.0, cameraAngle: 0.35, tilt: 0.055,
    rotationSpeed: 0.0003,
    lightIntensity: 3.5, ambientIntensity: 0,
    bloomStrength: 0.55, bloomThreshold: 0.62,
    metalness: 0.37, roughness: 1,
    cameraPos: { x: 1.714, y: -0.061, z: -1.745 },
  },
  saturn: {
    modelName: 'saturn', radius: 3.5, type: 'planet',
    cameraDist: 14.0, cameraAngle: 0.55, tilt: 0.388,
    rotationSpeed: 0.003,
    lightIntensity: 3.2, ambientIntensity: 0.15,
    bloomStrength: 0.55, bloomThreshold: 0.62,
    metalness: 0.3, roughness: 1,
    cameraPos: { x: 1.714, y: -0.061, z: -1.745 },
  },
  uranus: {
    modelName: 'uranus', radius: 2.8, type: 'planet',
    cameraDist: 9.5, cameraAngle: 0.45, tilt: 1.71,
    rotationSpeed: 0.003,
    lightIntensity: 4.2, ambientIntensity: 0.41,
    bloomStrength: 0.55, bloomThreshold: 0.62,
    metalness: 0.19, roughness: 1,
    cameraPos: { x: -0.331, y: -0.165, z: -2.339 },
    cameraTarget: { x: -0.498, y: 0.217, z: 0.099 },
  },
  neptune: {
    modelName: 'neptune', radius: 2.5, type: 'planet',
    cameraDist: 8.5, cameraAngle: 0.45, tilt: 0.49,
    rotationSpeed: 0.003,
    lightIntensity: 1.65, ambientIntensity: 0,
    bloomStrength: 0.55, bloomThreshold: 0.62,
    cameraPos: { x: 1.714, y: -0.061, z: -1.745 },
  },
  ceres: {
    modelName: 'ceres_v1.1', radius: 1.2, type: 'dwarf',
    cameraDist: 4.5, cameraAngle: 0.45, tilt: 0.07,
    rotationSpeed: 0.003,
    lightIntensity: 3.0, ambientIntensity: 0.5,
    bloomStrength: 0.55, bloomThreshold: 0.62,
    metalness: 0, roughness: 0.9,
    cameraPos: { x: 1.714, y: -0.061, z: -1.745 },
  },
  europa: {
    modelName: 'europa_v1.1', radius: 1.4, type: 'moon',
    cameraDist: 5.5, cameraAngle: 0.4, tilt: 0.03,
    rotationSpeed: 0.002,
    lightIntensity: 2.8, ambientIntensity: 0.4,
    bloomStrength: 0.55, bloomThreshold: 0.62,
    metalness: 0, roughness: 0.85,
    cameraPos: { x: 1.714, y: -0.061, z: -1.745 },
  },
  ganymede: {
    modelName: 'ganymede', radius: 1.6, type: 'moon',
    cameraDist: 6.0, cameraAngle: 0.4, tilt: 0.03,
    rotationSpeed: 0.002,
    lightIntensity: 2.5, ambientIntensity: 0.45,
    bloomStrength: 0.55, bloomThreshold: 0.62,
    metalness: 0, roughness: 0.8,
    cameraPos: { x: 1.714, y: -0.061, z: -1.745 },
  },
  the_moon: {
    modelName: 'the_moon', radius: 1.8, type: 'moon',
    cameraDist: 6.5, cameraAngle: 0.4, tilt: 0.11,
    rotationSpeed: 0.001,
    lightIntensity: 3.0, ambientIntensity: 0.3,
    bloomStrength: 0.55, bloomThreshold: 0.62,
    metalness: 0, roughness: 1,
    cameraPos: { x: 1.714, y: -0.061, z: -1.745 },
  },
  orrery: {
    modelName: 'orrery', radius: 6.0, type: 'orrery',
    cameraDist: 20.0, cameraAngle: 0.5, tilt: 0,
    rotationSpeed: 0.0005,
    lightIntensity: 2.0, ambientIntensity: 0.5,
    bloomStrength: 0.55, bloomThreshold: 0.62,
    metalness: 0, roughness: 0.7,
    cameraPos: { x: 4.091, y: 1.822, z: -6.655 },
    cameraTarget: { x: 3.679, y: 1.749, z: -6.382 },
  },
  blackhole: {
    modelName: null, radius: 0, type: 'blackhole',
    cameraDist: 12.0, cameraAngle: 0.3, tilt: 0,
    rotationSpeed: 0,
    lightIntensity: 0.0, ambientIntensity: 0.02,
    bloomStrength: 0.55, bloomThreshold: 0.62,
    metalness: 0, roughness: 1,
    cameraPos: { x: 0, y: 3, z: 10 },
    cameraTarget: { x: 0, y: 0, z: 0 },
  },
}

// ═══════════════════════════════════════════════════════════════
// STAR PALETTE
// ═══════════════════════════════════════════════════════════════

const STAR_PALETTE = [
  [1.0, 1.0, 1.0],
  [0.65, 0.82, 1.0],
  [0.5, 0.7, 1.0],
  [0.4, 0.6, 1.0],
  [1.0, 0.92, 0.75],
  [1.0, 0.8, 0.55],
  [1.0, 0.65, 0.5],
  [0.8, 0.65, 1.0],
  [0.95, 0.55, 0.7],
  [0.5, 1.0, 0.85],
]

const DISTANT_SUN_POS = new THREE.Vector3(-180, 60, -250)

// ═══════════════════════════════════════════════════════════════
// KHR_materials_pbrSpecularGlossiness PLUGIN
// Jupiter, Saturn, Uranus, Mercury (Sketchfab) use spec-gloss; r160 dropped support.
// ═══════════════════════════════════════════════════════════════

class PbrSpecGlossPlugin {
  constructor(parser) {
    this.parser = parser
    this.name = 'KHR_materials_pbrSpecularGlossiness'
  }

  getMaterialType(materialIndex) {
    const materialDef = this.parser.json.materials[materialIndex]
    if (!materialDef.extensions || !materialDef.extensions[this.name]) return null
    return THREE.MeshStandardMaterial
  }

  extendMaterialParams(materialIndex, params) {
    const parser = this.parser
    const materialDef = parser.json.materials[materialIndex]
    if (!materialDef.extensions || !materialDef.extensions[this.name]) {
      return Promise.resolve()
    }

    const ext = materialDef.extensions[this.name]
    const pending = []

    if (ext.diffuseFactor) {
      const d = ext.diffuseFactor
      params.color = new THREE.Color(d[0], d[1], d[2])
      params.opacity = d[3] !== undefined ? d[3] : 1.0
    }

    params.metalness = 0.0

    const gloss = ext.glossinessFactor !== undefined ? ext.glossinessFactor : 0.5
    params.roughness = Math.max(1.0 - gloss * 0.6, 0.4)

    if (ext.diffuseTexture !== undefined) {
      pending.push(
        parser.assignTexture(params, 'map', ext.diffuseTexture, THREE.SRGBColorSpace)
      )
    }

    return Promise.all(pending)
  }
}

// ═══════════════════════════════════════════════════════════════
// SCENE CLASS
// ═══════════════════════════════════════════════════════════════

export class CosmicScene {
  constructor(canvas) {
    log('CosmicScene constructor start', !!canvas)
    this.canvas = canvas
    this.currentGroup = null
    this.currentBodyId = null
    this.clock = new THREE.Clock()
    this.starMaterial = null
    this.starPoints = null
    this.distantSunGroup = null
    this.modelCache = new Map()
    this.gltfLoader = new GLTFLoader()
    this.gltfLoader.register(parser => new PbrSpecGlossPlugin(parser))
    this._savedCamPos = null
    this._savedCamTarget = null
    this._driftCallback = null

    this._initRenderer()
    log('CosmicScene: _initRenderer done')
    this._initControls()
    this._initPostProcessing()
    log('CosmicScene: composer ready')
    this._initLighting()
    this._buildStarField()
    log('CosmicScene: starfield built')
    this._createDistantSun()
    this._animate()
    log('CosmicScene constructor done')
  }

  _getEffectivePixelRatio() {
    const w = window.innerWidth
    const h = window.innerHeight
    const base = Math.min(window.devicePixelRatio, 2)
    const MAX_PIXELS = 3_500_000
    const total = w * h * base
    if (total > MAX_PIXELS) return Math.max(1, MAX_PIXELS / (w * h))
    return base
  }

  _initRenderer() {
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: false })
    this.renderer.setSize(window.innerWidth, window.innerHeight)
    this.renderer.setPixelRatio(this._getEffectivePixelRatio())
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping
    this.renderer.toneMappingExposure = 1.0
    this.renderer.outputColorSpace = THREE.SRGBColorSpace

    this.scene = new THREE.Scene()
    this.scene.background = new THREE.Color(0x000000)
    this.skyboxMesh = null
    const bgLoader = new THREE.TextureLoader()
    bgLoader.load('https://storage.googleapis.com/thecosmicstage-assets/backgrounds/starbox.jpeg', (tex) => {
      tex.colorSpace = THREE.SRGBColorSpace
      const skyGeo = new THREE.SphereGeometry(900, 64, 32)
      skyGeo.scale(-1, 1, 1)
      const skyMat = new THREE.ShaderMaterial({
        uniforms: {
          map: { value: tex },
          uBrightness: { value: 1.0 },
          uExposure: { value: 1.55 },
        },
        vertexShader: `
          varying vec2 vUv;
          void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: `
          uniform sampler2D map;
          uniform float uBrightness;
          uniform float uExposure;
          varying vec2 vUv;
          void main() {
            vec3 col = texture2D(map, vUv).rgb;
            col *= uBrightness;
            col = pow(col, vec3(1.0 / uExposure));
            gl_FragColor = vec4(col, 1.0);
          }
        `,
        depthWrite: false,
        side: THREE.FrontSide,
      })
      this.skyboxMesh = new THREE.Mesh(skyGeo, skyMat)
      this.skyboxMesh.renderOrder = -1
      this.skyboxMesh.rotation.set(0, 0.038, 0)
      this.scene.add(this.skyboxMesh)
      this.scene.background = null
    })

    this.camera = new THREE.PerspectiveCamera(50, window.innerWidth / window.innerHeight, 0.1, 1000)

    window.addEventListener('resize', () => {
      const w = window.innerWidth
      const h = window.innerHeight
      this.camera.aspect = w / h
      this.camera.updateProjectionMatrix()
      this.renderer.setPixelRatio(this._getEffectivePixelRatio())
      this.renderer.setSize(w, h)
      this.composer.setSize(w, h)
      this.bloomPass.setSize(w, h)

      if (this.currentGroup && BODY_3D[this.currentBodyId]?.type === 'blackhole') {
        this.currentGroup.traverse(child => {
          if (child.userData?.bhParticles && child.material?.uniforms?.uViewHeight) {
            child.material.uniforms.uViewHeight.value = h
          }
        })
      }
    })
  }

  _initControls() {
    this.controls = new OrbitControls(this.camera, this.canvas)
    this.controls.enableDamping = true
    this.controls.dampingFactor = 0.05
    this.controls.autoRotate = false
    this.controls.autoRotateSpeed = 0
    this.controls.enablePan = true
    this.controls.panSpeed = 0.8
    this.controls.screenSpacePanning = true
    this.controls.minDistance = 0.5
    this.controls.maxDistance = 500
    this.controls.mouseButtons = {
      LEFT: THREE.MOUSE.ROTATE,
      MIDDLE: THREE.MOUSE.PAN,
      RIGHT: THREE.MOUSE.PAN,
    }
    this.controls.touches = {
      ONE: THREE.TOUCH.ROTATE,
      TWO: THREE.TOUCH.DOLLY_PAN,
    }
    if (isMobile()) {
      this.controls.rotateSpeed = 0.6
      this.controls.zoomSpeed = 0.8
      this.controls.panSpeed = 0.5
    }
    this.controls.enabled = false

    this.canvas.addEventListener('pointerdown', (e) => {
      if (e.shiftKey) {
        this.controls.mouseButtons.LEFT = THREE.MOUSE.PAN
      } else {
        this.controls.mouseButtons.LEFT = THREE.MOUSE.ROTATE
      }
    }, true)

    const raycaster = new THREE.Raycaster()
    const pointer = new THREE.Vector2()
    this.canvas.addEventListener('dblclick', (e) => {
      if (!this.controls.enabled || !this.currentGroup) return
      pointer.x = (e.clientX / window.innerWidth) * 2 - 1
      pointer.y = -(e.clientY / window.innerHeight) * 2 + 1
      raycaster.setFromCamera(pointer, this.camera)
      const hits = raycaster.intersectObjects(this.currentGroup.children, true)
      if (hits.length > 0) {
        const data = BODY_3D[this.currentBodyId]
        const tgt = data?.cameraTarget || DEFAULT_TARGET
        this.controls.target.set(tgt.x, tgt.y, tgt.z)
        this.controls.update()
      }
    })
  }

  _initPostProcessing() {
    this.composer = new EffectComposer(this.renderer)
    this.composer.addPass(new RenderPass(this.scene, this.camera))
    this.bloomPass = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      0.55, 1.25, 0.62,
    )
    this.composer.addPass(this.bloomPass)
    this.composer.addPass(new OutputPass())
  }

  _initLighting() {
    this.ambientLight = new THREE.AmbientLight(0x8899bb, 0.12)
    this.scene.add(this.ambientLight)
    this.sunLight = new THREE.DirectionalLight(0xffeedd, 2.0)
    this.sunLight.position.set(-8, 4, -6)
    this.scene.add(this.sunLight)
  }

  _buildStarField() {
    if (this.starPoints) this.scene.remove(this.starPoints)

    const count = 50000, minSize = 2.5, maxSize = 2.9
    const spread = 300, depth = 700, brightness = 3, colorSaturation = 0.65
    const positions = new Float32Array(count * 3)
    const colors = new Float32Array(count * 3)
    const sizes = new Float32Array(count)
    const phases = new Float32Array(count)

    for (let i = 0; i < count; i++) {
      const theta = Math.random() * Math.PI * 2
      const phi = Math.acos(2 * Math.random() - 1)
      const r = spread + Math.random() * depth
      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta)
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta)
      positions[i * 3 + 2] = r * Math.cos(phi)

      const base = STAR_PALETTE[Math.floor(Math.random() * STAR_PALETTE.length)]
      const r0 = 1.0 + (base[0] - 1.0) * colorSaturation
      const g0 = 1.0 + (base[1] - 1.0) * colorSaturation
      const b0 = 1.0 + (base[2] - 1.0) * colorSaturation
      const boost = brightness * (0.85 + Math.random() * 0.3)
      colors[i * 3] = Math.min(r0 * boost, 1.0)
      colors[i * 3 + 1] = Math.min(g0 * boost, 1.0)
      colors[i * 3 + 2] = Math.min(b0 * boost, 1.0)

      sizes[i] = minSize + Math.random() * (maxSize - minSize)
      phases[i] = Math.random()
    }

    const geom = new THREE.BufferGeometry()
    geom.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geom.setAttribute('aColor', new THREE.BufferAttribute(colors, 3))
    geom.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1))
    geom.setAttribute('aPhase', new THREE.BufferAttribute(phases, 1))

    if (!this.starMaterial) {
      this.starMaterial = new THREE.ShaderMaterial({
        uniforms: {
          uTime: { value: 0 },
          uPointScale: { value: 520 },
          uMaxSize: { value: 13.5 },
        },
        vertexShader: `
          attribute float aSize;
          attribute float aPhase;
          attribute vec3 aColor;
          uniform float uTime;
          uniform float uPointScale;
          uniform float uMaxSize;
          varying float vAlpha;
          varying vec3 vColor;
          void main() {
            vColor = aColor;
            vec4 mvPos = modelViewMatrix * vec4(position, 1.0);
            float speed = 0.4 + aPhase * 1.8;
            float twinkle = sin(uTime * speed + aPhase * 6.283) * 0.35 + 0.65;
            twinkle *= 0.75 + sin(uTime * speed * 0.37 + aPhase * 4.5) * 0.25;
            vAlpha = twinkle;
            gl_PointSize = clamp(aSize * twinkle * (uPointScale / -mvPos.z), 1.5, uMaxSize);
            gl_Position = projectionMatrix * mvPos;
          }
        `,
        fragmentShader: `
          varying float vAlpha;
          varying vec3 vColor;
          void main() {
            vec2 uv = gl_PointCoord - vec2(0.5);
            float d = dot(uv, uv);
            if (d > 0.25) discard;
            float core = exp(-d * 50.0);
            float soft = exp(-d * 8.0) * 0.4;
            float outer = exp(-d * 3.0) * 0.1;
            gl_FragColor = vec4(vColor, (core + soft + outer) * vAlpha);
          }
        `,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      })
    }

    this.starPoints = new THREE.Points(geom, this.starMaterial)
    this.scene.add(this.starPoints)
  }

  _createFlareTexture(size, innerColor, outerColor, peak) {
    const c = document.createElement('canvas')
    c.width = c.height = size
    const ctx = c.getContext('2d')
    const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2)
    g.addColorStop(0, innerColor)
    g.addColorStop(peak, outerColor)
    g.addColorStop(1, 'rgba(0,0,0,0)')
    ctx.fillStyle = g
    ctx.fillRect(0, 0, size, size)
    return new THREE.CanvasTexture(c)
  }

  _createDistantSun() {
    this.distantSunGroup = new THREE.Group()
    const sunMat = new THREE.MeshBasicMaterial({
      color: new THREE.Color(1.8, 1.4, 0.8),
      toneMapped: false,
    })
    this.distantSunGroup.add(new THREE.Mesh(new THREE.SphereGeometry(2.5, 24, 24), sunMat))

    const flareCore = this._createFlareTexture(128, 'rgba(255,250,230,0.9)', 'rgba(255,200,100,0.0)', 0.08)
    const flareSoft = this._createFlareTexture(256, 'rgba(255,220,160,0.15)', 'rgba(255,180,80,0.0)', 0.2)
    const flareGhost = this._createFlareTexture(64, 'rgba(140,180,255,0.08)', 'rgba(100,140,255,0.0)', 0.5)

    const lensflare = new Lensflare()
    lensflare.addElement(new LensflareElement(flareCore, 120, 0))
    lensflare.addElement(new LensflareElement(flareSoft, 300, 0))
    lensflare.addElement(new LensflareElement(flareGhost, 20, 0.5))
    lensflare.addElement(new LensflareElement(flareGhost, 15, 0.8))

    this.distantSunGroup.add(lensflare)
    this.distantSunGroup.position.copy(DISTANT_SUN_POS)
    this.scene.add(this.distantSunGroup)
  }

  // ─── Model Loading ───

  _loadGLB(name, onProgress) {
    return new Promise((resolve, reject) => {
      if (this.modelCache.has(name)) {
        resolve(this.modelCache.get(name).clone())
        return
      }
      this.gltfLoader.load(
        `${ASSET_BASE}/${name}.glb`,
        (gltf) => {
          this.modelCache.set(name, gltf.scene)
          resolve(gltf.scene.clone())
        },
        (xhr) => {
          if (onProgress && xhr.total > 0) onProgress(xhr.loaded / xhr.total)
        },
        (err) => {
          console.warn(`Failed to load ${name}.glb:`, err)
          reject(err)
        },
      )
    })
  }

  _fixMaterials(model) {
    model.traverse((child) => {
      if (child.isMesh) {
        const mats = Array.isArray(child.material) ? child.material : [child.material]
        for (const mat of mats) {
          if (mat.map) mat.map.colorSpace = THREE.SRGBColorSpace
          if (mat.emissiveMap) mat.emissiveMap.colorSpace = THREE.SRGBColorSpace
          mat.needsUpdate = true
        }
      }
    })
  }

  _applyBodyMaterialOverrides(model, data) {
    if (data.metalness === undefined && data.roughness === undefined) return
    model.traverse((child) => {
      if (child.isMesh) {
        const mats = Array.isArray(child.material) ? child.material : [child.material]
        for (const mat of mats) {
          if (data.metalness !== undefined) mat.metalness = data.metalness
          if (data.roughness !== undefined) mat.roughness = data.roughness
        }
      }
    })
  }

  _normalizeModel(model, targetRadius, data) {
    this._fixMaterials(model)
    if (data) this._applyBodyMaterialOverrides(model, data)
    const box = new THREE.Box3().setFromObject(model)
    const sphere = new THREE.Sphere()
    box.getBoundingSphere(sphere)
    const scale = targetRadius / sphere.radius
    model.scale.setScalar(scale)
    model.position.copy(sphere.center).multiplyScalar(-scale)
  }

  _generateNoiseTexture(width, height) {
    const data = new Uint8Array(width * height * 4)
    const freq = 8
    const grid = {}
    const getGrid = (gx, gy, seed) => {
      const wx = ((gx % freq) + freq) % freq
      const wy = ((gy % freq) + freq) % freq
      const key = `${wx},${wy},${seed}`
      if (!(key in grid)) {
        let n = wx * 127.1 + wy * 311.7 + seed * 997.3
        n = Math.sin(n * 43758.5453 + 12.9898) * 43758.5453
        grid[key] = n - Math.floor(n)
      }
      return grid[key]
    }
    const smoothNoise = (u, v, seed) => {
      const fx = u * freq, fy = v * freq
      const ix = Math.floor(fx), iy = Math.floor(fy)
      const dx = fx - ix, dy = fy - iy
      const sx = dx * dx * (3 - 2 * dx)
      const sy = dy * dy * (3 - 2 * dy)
      const n00 = getGrid(ix, iy, seed)
      const n10 = getGrid(ix + 1, iy, seed)
      const n01 = getGrid(ix, iy + 1, seed)
      const n11 = getGrid(ix + 1, iy + 1, seed)
      return n00 * (1 - sx) * (1 - sy) + n10 * sx * (1 - sy) +
             n01 * (1 - sx) * sy + n11 * sx * sy
    }
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const u = x / width, v = y / height
        const idx = (y * width + x) * 4
        data[idx]     = Math.floor(smoothNoise(u, v, 0) * 255)
        data[idx + 1] = Math.floor(smoothNoise(u, v, 100) * 255)
        data[idx + 2] = Math.floor(smoothNoise(u, v, 200) * 255)
        data[idx + 3] = 255
      }
    }
    const texture = new THREE.DataTexture(data, width, height)
    texture.format = THREE.RGBAFormat
    texture.wrapS = THREE.RepeatWrapping
    texture.wrapT = THREE.RepeatWrapping
    texture.needsUpdate = true
    return texture
  }

  _buildBlackHole() {
    const g = new THREE.Group()
    const noiseTexture = this._generateNoiseTexture(128, 128)

    const innerColor = new THREE.Color('#616161')
    const outerColor = new THREE.Color('#111218')

    // Accretion disc — CylinderGeometry cone (from Black Hole project)
    const discGeometry = new THREE.CylinderGeometry(5, 1, 0, 64, 10, true)
    const discMaterial = new THREE.RawShaderMaterial({
      glslVersion: THREE.GLSL3,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
      transparent: true,
      uniforms: {
        uTime: { value: 0 },
        uNoiseTexture: { value: noiseTexture },
        uInnerColor: { value: innerColor },
        uOuterColor: { value: outerColor },
        uInnerEdge: { value: 0.0 },
      },
      vertexShader: BH_DISC_VERT,
      fragmentShader: BH_DISC_FRAG,
    })
    const disc = new THREE.Mesh(discGeometry, discMaterial)
    disc.userData.bhDisc = true
    g.add(disc)

    // Particles — 150K points orbiting the singularity
    const PARTICLE_COUNT = 150000
    const distanceArray = new Float32Array(PARTICLE_COUNT)
    const sizeArray = new Float32Array(PARTICLE_COUNT)
    const randomArray = new Float32Array(PARTICLE_COUNT)
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      distanceArray[i] = Math.random()
      sizeArray[i] = Math.random()
      randomArray[i] = Math.random()
    }

    const particleGeo = new THREE.BufferGeometry()
    particleGeo.setAttribute('aDistance', new THREE.Float32BufferAttribute(distanceArray, 1))
    particleGeo.setAttribute('aSize', new THREE.Float32BufferAttribute(sizeArray, 1))
    particleGeo.setAttribute('aRandom', new THREE.Float32BufferAttribute(randomArray, 1))

    const particleMaterial = new THREE.RawShaderMaterial({
      glslVersion: THREE.GLSL3,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
      transparent: true,
      uniforms: {
        uTime: { value: 0 },
        uInnerColor: { value: innerColor },
        uOuterColor: { value: outerColor },
        uViewHeight: { value: window.innerHeight },
        uSize: { value: 0.015 },
        uNumSwarms: { value: 2.0 },
        uDiscWidth: { value: 12.0 },
      },
      vertexShader: BH_PARTICLE_VERT,
      fragmentShader: BH_PARTICLE_FRAG,
    })

    const particles = new THREE.Points(particleGeo, particleMaterial)
    particles.frustumCulled = false
    particles.userData.bhParticles = true
    g.add(particles)

    return g
  }

  // ─── Public API ───

  async loadBody(bodyId, onProgress) {
    const data = BODY_3D[bodyId]
    if (!data) return null

    const group = new THREE.Group()
    group.rotation.z = data.tilt || 0
    if (data.bodyRot) {
      group.rotation.x = data.bodyRot.x || 0
      group.rotation.y = data.bodyRot.y || 0
      group.rotation.z = data.bodyRot.z || 0
    }

    if (data.type === 'blackhole') {
      const bhGroup = this._buildBlackHole()
      bhGroup.name = 'body'
      group.add(bhGroup)
    } else if (data.modelName) {
      try {
        const model = await this._loadGLB(data.modelName, onProgress)
        this._normalizeModel(model, data.radius, data)
        model.name = 'body'
        group.add(model)
      } catch {
        const mat = new THREE.MeshPhongMaterial({ color: 0x888888, shininess: 10 })
        const mesh = new THREE.Mesh(new THREE.SphereGeometry(data.radius, 48, 48), mat)
        mesh.name = 'body'
        group.add(mesh)
      }
    }
    return group
  }

  showBody(group, bodyId) {
    this.removeCurrentBody()
    const data = BODY_3D[bodyId]
    if (!data) return

    this.currentGroup = group
    this.currentBodyId = bodyId
    this.scene.add(group)

    this.sunLight.intensity = data.lightIntensity ?? 2.0
    this.sunLight.position.copy(DISTANT_SUN_POS).normalize().multiplyScalar(10)
    this.ambientLight.intensity = data.ambientIntensity ?? 0.12
    this.bloomPass.strength = data.bloomStrength ?? 0.55
    this.bloomPass.threshold = data.bloomThreshold ?? 0.62
    if (this.distantSunGroup) this.distantSunGroup.visible = data.type !== 'star' && data.type !== 'blackhole'

    this._positionCamera(data)
    this.markCameraHome()
  }

  removeCurrentBody() {
    if (!this.currentGroup) return
    this.scene.remove(this.currentGroup)
    this.currentGroup = null
    this.currentBodyId = null
  }

  enableControls() {
    this.controls.enabled = true
  }

  disableControls() {
    this.controls.enabled = false
  }

  setCamera(pos, target) {
    const tgt = target || DEFAULT_TARGET
    this.camera.position.set(pos.x, pos.y, pos.z)
    this.controls.target.set(tgt.x, tgt.y, tgt.z)
    this.camera.lookAt(tgt.x, tgt.y, tgt.z)
    this.controls.update()
  }

  getCameraConfig(bodyId) {
    const data = BODY_3D[bodyId]
    if (!data) return null
    const mobile = isMobile()
    const scale = mobile ? MOBILE_CAM_PULLBACK : 1
    const pos = data.cameraPos
      ? { x: data.cameraPos.x * scale, y: data.cameraPos.y * scale, z: data.cameraPos.z * scale }
      : (() => {
          const dist = data.cameraDist * scale
          const angle = data.cameraAngle || 0.5
          return {
            x: dist * Math.cos(angle) * Math.sin(0.8),
            y: dist * Math.sin(angle),
            z: dist * Math.cos(angle) * Math.cos(0.8),
          }
        })()
    const tgt = data.cameraTarget || DEFAULT_TARGET
    return { pos, target: { x: tgt.x, y: tgt.y, z: tgt.z } }
  }

  animateCamera(endPos, endTarget, duration = 4000) {
    return new Promise((resolve) => {
      const startPos = this.camera.position.clone()
      const startTarget = this.controls.target.clone()
      const ep = new THREE.Vector3(endPos.x, endPos.y, endPos.z)
      const et = new THREE.Vector3(endTarget.x, endTarget.y, endTarget.z)
      const t0 = performance.now()

      const step = () => {
        const t = Math.min((performance.now() - t0) / duration, 1)
        const e = t * t * (3 - 2 * t)
        this.camera.position.lerpVectors(startPos, ep, e)
        this.controls.target.lerpVectors(startTarget, et, e)
        this.controls.update()
        if (t < 1) requestAnimationFrame(step)
        else resolve()
      }
      step()
    })
  }

  markCameraHome() {
    this._savedCamPos = this.camera.position.clone()
    this._savedCamTarget = this.controls.target.clone()
  }

  getCameraDrift() {
    if (!this._savedCamPos) return 0
    const posDrift = this.camera.position.distanceTo(this._savedCamPos)
    const tgtDrift = this.controls.target.distanceTo(this._savedCamTarget)
    return Math.max(posDrift, tgtDrift)
  }

  resetToHome() {
    if (!this._savedCamPos) return
    this.camera.position.copy(this._savedCamPos)
    this.controls.target.copy(this._savedCamTarget)
    this.controls.update()
  }

  focusOnBody() {
    this.controls.target.set(0, 0, 0)
    this.controls.update()
  }

  setDriftCallback(cb) {
    this._driftCallback = cb
  }

  fadeInLights(bodyId, duration = 4000) {
    const data = BODY_3D[bodyId]
    if (!data) return Promise.resolve()

    const targetLight = data.lightIntensity ?? 2.0
    const targetAmbient = data.ambientIntensity ?? 0.12
    this.sunLight.intensity = 0
    this.ambientLight.intensity = 0

    return new Promise((resolve) => {
      const t0 = performance.now()
      const step = () => {
        const t = Math.min((performance.now() - t0) / duration, 1)
        const e = t * t * (3 - 2 * t)
        this.sunLight.intensity = targetLight * e
        this.ambientLight.intensity = targetAmbient * e
        if (t < 1) requestAnimationFrame(step)
        else {
          this.sunLight.intensity = targetLight
          this.ambientLight.intensity = targetAmbient
          resolve()
        }
      }
      step()
    })
  }

  darkenLights() {
    this.sunLight.intensity = 0
    this.ambientLight.intensity = 0
  }

  rebuildStarField() {
    this._buildStarField()
  }

  _positionCamera(data) {
    const pos = data.cameraPos
      ? new THREE.Vector3(data.cameraPos.x, data.cameraPos.y, data.cameraPos.z)
      : (() => {
          const dist = data.cameraDist
          const angle = data.cameraAngle || 0.5
          return new THREE.Vector3(
            dist * Math.cos(angle) * Math.sin(0.8),
            dist * Math.sin(angle),
            dist * Math.cos(angle) * Math.cos(0.8),
          )
        })()
    const tgt = data.cameraTarget || DEFAULT_TARGET
    const target = new THREE.Vector3(tgt.x, tgt.y, tgt.z)
    this.camera.position.copy(pos)
    this.controls.target.copy(target)
    this.camera.lookAt(target)
    this.controls.update()
  }

  _animate() {
    let frameCount = 0
    const loop = () => {
      requestAnimationFrame(loop)
      frameCount++
      if (frameCount === 1) log('animate: first frame')
      if (frameCount === 60) log('animate: 60 frames (~1s)')
      const elapsed = this.clock.getElapsedTime()

      if (this.currentGroup) {
        const data = BODY_3D[this.currentBodyId]
        if (data) {
          const body = this.currentGroup.getObjectByName('body')
          if (body) body.rotateY(data.rotationSpeed)
        }
      }

      if (this.starMaterial) this.starMaterial.uniforms.uTime.value = elapsed

      if (this.currentGroup && BODY_3D[this.currentBodyId]?.type === 'blackhole') {
        this.currentGroup.traverse(child => {
          if (child.userData?.bhDisc && child.material?.uniforms?.uTime) {
            child.material.uniforms.uTime.value = elapsed * 1.75
          }
          if (child.userData?.bhParticles && child.material?.uniforms?.uTime) {
            child.material.uniforms.uTime.value = (elapsed + 9999.0) * 0.1
          }
        })
      }

      if (this.currentBodyId && BODY_3D[this.currentBodyId]?.type === 'star') {
        const data = BODY_3D[this.currentBodyId]
        const baseLight = data.lightIntensity ?? 1.35
        const baseAmbient = data.ambientIntensity ?? 0.9
        const baseBloom = data.bloomStrength ?? 0.55
        const baseThresh = data.bloomThreshold ?? 0.62

        const p1 = Math.sin(elapsed * 0.5) * 0.5 + 0.5
        const p2 = Math.sin(elapsed * 0.8 + 1.3) * 0.5 + 0.5
        const p3 = Math.sin(elapsed * 0.3 + 2.7) * 0.5 + 0.5
        const flicker = Math.sin(elapsed * 2.5) * 0.005

        this.sunLight.intensity = baseLight + p1 * 0.04 + flicker
        this.ambientLight.intensity = baseAmbient + p2 * 0.02
        this.bloomPass.strength = baseBloom + p3 * 0.03 + flicker
        this.bloomPass.threshold = baseThresh - p1 * 0.01
      }

      if (this.controls.enabled) {
        this.controls.update()
        if (this._savedCamPos && this._driftCallback) {
          this._driftCallback(this.getCameraDrift())
        }
      }

      this.composer.render()
    }
    loop()
  }
}
